import { useState, useEffect, useCallback } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import actionsHandler from "@/store/actions/actionHandler";
import dateHandler from "@/composables/dateUtil";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { toast } from "react-toastify";
import CustomTable from "@/components/common/table/CustomTable";
import { useParams, useNavigate } from "react-router-dom";
import FileIcon from "@styles/all-images/icons/file.svg";

interface DocumentHistoryData {
  id: number;
  file_name: string;
  action_at: string;
  // ... other fields
}

const OnboardingDocumentHistory = () => {
  const { formatDate } = dateHandler();
  const { id } = useParams();
  const [forceRefresh, setForceRefresh] = useState(false);
  const navigate = useNavigate();

  const dispatch = useDispatch<AppThunkDispatch>();

  useEffect(() => {
    if (forceRefresh) {
      setForceRefresh(false); // Just reset the flag, CustomTable will handle the refresh
    }
  }, [forceRefresh]);

  const handleView = async (id: number) => {
    try {
      const currentPathName = window.location.pathname;
      navigate(
        `/t4/view-onboarding-document/view/${id}?source=history&from=${currentPathName}`,
      );
    } catch (error) {
      console.error("Error fetching task details:", error);
    }
  };

  const handleFetch = useCallback(
    async (params: {
      page: string;
      limit: string;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { authenticatedCall } = actionsHandler(
          "viewOnboardingDocumentHistory",
        );

        const queryParams = new URLSearchParams({
          limit: params.limit,
          page: params.page,
          search: params.search || "",
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
        }).toString();

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [`${id}/`],
            customQuery: queryParams,
          }),
        );

        if (response.ok) {
          console.log("response", response);
          return {
            ...response,
            data: response.data.data,
          };
        } else {
          toast.error(
            response.data.message || "Failed to fetch document history",
          );
        }
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    },
    [dispatch, id],
  );

  // Define columns for CustomTable
  const columns = [
    {
      header: "Document Name",
      accessor: (row: DocumentHistoryData) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#F4EBFF]">
            <img
              src={FileIcon}
              alt={"File Icon"}
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-5 tw-max-h-5"
            />
          </span>
          <span className="tw-truncate tw-max-w-[300px]">{row.file_name}</span>
        </div>
      ),
      sortable: true,
      sortKey: "file_name",
    },
    {
      header: "Re-Uploaded on",
      accessor: (row: DocumentHistoryData) => {
        return row?.action_at ? formatDate(row.action_at) : "-";
      },
      sortable: true,
      sortKey: "action_at",
    },
    {
      header: "Action",
      accessor: (row: DocumentHistoryData) => (
        <div className="tw-flex tw-items-center tw-justify-left tw-gap-4">
          <button
            onClick={() => handleView(row.id)}
            className="tw-text-[#146C94] hover:tw-text-[#105C84]"
          >
            <img
              src={EyeIcon}
              alt="View Icon"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a onClick={() => navigate(-1)}>
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Change History
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-mt-[147px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          noDataMessage="No documents found"
          noSearchDataMessage="No matching documents found"
        />
      </div>
    </div>
  );
};

export default OnboardingDocumentHistory;
